import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { setSharedSessionCookie, clearSharedSessionCookie } from '../lib/sharedSession';

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.slice(name.length + 1));
}

const COLORS = {
  bg: '#0F0B18',
  bgPanel: '#1A1326',
  gold: '#C9922A',
  text: '#F5EFE0',
  textMuted: '#A89B7E',
  error: '#E08585',
  border: '#2D2238',
};

function Shell({ children }) {
  return (
    <div
      className="min-h-screen-dvh"
      style={{
        background: COLORS.bg,
        color: COLORS.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'DM Sans, sans-serif',
        padding: 24,
      }}
    >
      {children}
    </div>
  );
}

function LoginForm({ onAuthenticated }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (signInError || !data?.session) {
        setError('Invalid email or password');
        return;
      }
      setSharedSessionCookie(data.session);
      onAuthenticated(data.session);
    } catch {
      setError('Invalid email or password');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        width: '100%',
        maxWidth: 420,
        background: COLORS.bgPanel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        padding: 32,
        boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div
          style={{
            color: COLORS.gold,
            fontSize: 13,
            letterSpacing: 3,
            fontWeight: 600,
            textTransform: 'uppercase',
          }}
        >
          NNHC
        </div>
        <h1
          style={{
            color: COLORS.text,
            margin: '8px 0 4px',
            fontSize: 24,
            fontWeight: 600,
          }}
        >
          Clinical Portal
        </h1>
        <div style={{ color: COLORS.textMuted, fontSize: 14 }}>
          Sign in to continue
        </div>
      </div>

      <label style={{ display: 'block', marginBottom: 16 }}>
        <span
          style={{
            display: 'block',
            color: COLORS.textMuted,
            fontSize: 12,
            letterSpacing: 1,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Email
        </span>
        <input
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 14px',
            minHeight: 44,
            background: COLORS.bg,
            color: COLORS.text,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 8,
            fontSize: 16,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </label>

      <label style={{ display: 'block', marginBottom: 16 }}>
        <span
          style={{
            display: 'block',
            color: COLORS.textMuted,
            fontSize: 12,
            letterSpacing: 1,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Password
        </span>
        <input
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 14px',
            minHeight: 44,
            background: COLORS.bg,
            color: COLORS.text,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 8,
            fontSize: 16,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </label>

      {error ? (
        <div
          role="alert"
          style={{
            color: COLORS.error,
            fontSize: 14,
            marginBottom: 16,
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      ) : null}

      <button
        type="submit"
        disabled={submitting}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: COLORS.gold,
          color: COLORS.bg,
          border: 'none',
          borderRadius: 8,
          fontSize: 15,
          fontWeight: 600,
          letterSpacing: 1,
          textTransform: 'uppercase',
          cursor: submitting ? 'not-allowed' : 'pointer',
          opacity: submitting ? 0.7 : 1,
        }}
      >
        {submitting ? 'Signing in…' : 'Sign In'}
      </button>
    </form>
  );
}

export default function AuthGuard({ children }) {
  // [clinical-diag] Temporary diagnostics — see PR. (Runs on every render,
  // including React.StrictMode's intentional double-invoke in dev.)
  console.log('[clinical-diag] AuthGuard mount start');
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    let cancelled = false;

    // [clinical-diag] Temporary diagnostics — see PR.
    console.log('[clinical-diag] AuthGuard mounted — status=checking; starting session check');

    // Safety valve: never show spinner longer than 5 seconds
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      console.warn('[clinical-diag] AuthGuard 5s safety timeout fired — session check did not resolve; forcing login form');
      setStatus((prev) => (prev === 'checking' ? 'login' : prev));
    }, 5000);

    async function check() {
      // Step 1: check for an active Supabase session
      console.log('[clinical-diag] AuthGuard: calling supabase.auth.getSession()');
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      console.log('[clinical-diag] AuthGuard: getSession() resolved —', session ? 'session present' : 'no active session');

      if (session) {
        // [clinical-diag] Temporary diagnostics — see PR.
        console.log('[clinical-diag] AuthGuard session resolved');
        setSharedSessionCookie(session);
        setStatus('authenticated');
        return;
      }

      // Step 2: check for nnhc_session SSO cookie (set by Command Center)
      const raw = readCookie('nnhc_session');
      console.log('[clinical-diag] AuthGuard: nnhc_session cookie —', raw ? 'present' : 'absent');
      if (!raw) {
        setStatus('login');
        return;
      }

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        console.warn('[clinical-diag] AuthGuard: nnhc_session cookie failed to JSON.parse —', e);
        setStatus('login');
        return;
      }

      const { access_token, refresh_token } = parsed || {};
      if (!access_token || !refresh_token) {
        console.warn('[clinical-diag] AuthGuard: nnhc_session cookie missing access/refresh token');
        setStatus('login');
        return;
      }

      console.log('[clinical-diag] AuthGuard: calling supabase.auth.setSession() — network call to <supabase-url>/auth/v1/user');
      const { data: setData, error: setError } = await supabase.auth.setSession({
        access_token,
        refresh_token,
      });
      if (cancelled) return;
      console.log('[clinical-diag] AuthGuard: setSession() resolved —', setError ? `error: ${setError.message}` : (setData?.session ? 'session established' : 'no session returned'));

      if (setError || !setData?.session) {
        setStatus('login');
        return;
      }

      setSharedSessionCookie(setData.session);
      setStatus('authenticated');
    }

    check().catch((e) => {
      console.error('[clinical-diag] AuthGuard: session check threw (was previously swallowed silently) —', e);
      if (!cancelled) setStatus((prev) => (prev === 'checking' ? 'login' : prev));
    });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  // Currently unused: ClinicalPortal handles its own logout (supabase.auth.signOut).
  // Retained for a future AuthGuard-driven sign-out path. The eslint-disable is
  // needed because this file is now compiled by react-scripts (CI=true treats
  // the no-unused-vars warning as an error) — see fix PR.
  // eslint-disable-next-line no-unused-vars
  async function handleSignOut() {
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore
    }
    clearSharedSessionCookie();
    setStatus('login');
  }

  if (status === 'authenticated') return children;

  if (status === 'login') {
    return (
      <Shell>
        <LoginForm onAuthenticated={() => setStatus('authenticated')} />
      </Shell>
    );
  }

  // 'checking' — waiting for session resolution
  return (
    <Shell>
      <div style={{ color: COLORS.gold, fontSize: 18 }}>Verifying session…</div>
    </Shell>
  );
}
